import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export function isSliderComponent(obj: HTMLElement): obj is SliderComponent {
	if (!obj) return false
	if (obj.localName !== 'slider-component') return false
	return obj instanceof SliderComponent
}

export class SliderComponent extends UcoastEl {
	static htmlSelector = 'slider-component'
	slider: HTMLElement
	sliderItems?: NodeListOf<HTMLElement>
	sliderItemsToShow?: HTMLElement[]
	sliderItemOffset?: number
	slidesPerPage?: number
	totalPages?: number
	currentPage?: number = 1
	carouselWidth?: number = 0
	slideScrollPosition?: number
	enableSliderLooping: boolean
	currentPageElement?: HTMLElement
	pageTotalElement?: HTMLElement
	prevButton?: HTMLButtonElement
	nextButton?: HTMLButtonElement
	carousel?: HTMLElement
	carouselContainer?: HTMLElement
	mediaLoaded = false
	autoplaySpeed = 0
	autoplay?: number
	autoplayObserver?: IntersectionObserver
	autoplayEnabled = false
	reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
	private onAutoplayMouseEnterBound = this.pauseAutoplay.bind(this)
	private onAutoplayMouseLeaveBound = this.playAutoplay.bind(this)
	private onAutoplayFocusInBound = this.pauseAutoplay.bind(this)
	private onAutoplayFocusOutBound = this.playAutoplay.bind(this)
	constructor() {
		super()
		this.slider = q.rs('[id^="Slider-"]', this)
		this.sliderItems = q.ol('[id^="Slide-"]', this)
		this.enableSliderLooping = this.hasAttribute('data-loop')
		this.currentPageElement = q.os('.slider-counter--current', this)
		this.pageTotalElement = q.os('.slider-counter--total', this)
		this.prevButton = q.os('button[name="previous"]', this)
		this.nextButton = q.os('button[name="next"]', this)
		this.carousel = q.os('.slider-carousel__thumb', this)
		this.carouselContainer = q.os('.slider-carousel', this)

		this.initPages()
		const resizeObserver = new ResizeObserver((_entries: ResizeObserverEntry[]) =>
			this.initPages()
		)
		resizeObserver.observe(this.slider)

		this.slider.addEventListener('scroll', this.update.bind(this))
		this.prevButton?.addEventListener('click', this.onButtonClick.bind(this))
		this.nextButton?.addEventListener('click', this.onButtonClick.bind(this))
		this.setupAutoplay()
	}

	initPages() {
		this.sliderItemsToShow = this.sliderItems ? Array.from(this.sliderItems).filter(
			(element) => element.clientWidth > 0
		) : []
		if (this.sliderItemsToShow.length < 2) return
		this.sliderItemOffset =
			this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft
		this.slidesPerPage = Math.floor(
			(this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
		)
		this.totalPages = this.sliderItemsToShow.length
		this.setCarousel();
		this.update()
	}

	setCarousel() {
		if (this.carouselContainer && this.carousel && this.currentPage && this.totalPages) {
			this.carouselWidth = this.carouselContainer?.clientWidth / this.totalPages;
			this.carousel.style.width = this.carouselWidth + "px";
			this.carousel.style.marginLeft = (this.currentPage - 1) * this.carouselWidth + "px";
		}
	}

	setActive() {
		this.sliderItems?.forEach((item, index)=> {
			if (index == this.currentPage - 1) {
				item.classList.add('is-active');
			} else {
				item.classList.remove('is-active');
			}
		})
	}

	resetPages() {
		this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
		this.initPages()
	}

	createSlideChangedEvent(): SlideChangedEvent {
		this.loadMedia()
		const currentPage = this.currentPage
		if (!currentPage)
			throw new Error('cannot createSlideChangedEvent - currentPage is undefined')
		if (!this.sliderItemsToShow)
			throw new Error('cannot createSlideChangedEvent - sliderItemsToShow is undefined')
		const currentElement = this.sliderItemsToShow[currentPage - 1]
		if (!currentElement)
			throw new Error('cannot createSlideChangedEvent - currentElement is undefined')
		return new CustomEvent('slideChanged', {
			detail: {
				currentPage,
				currentElement,
			},
		})
	}

	update() {
		// Temporarily prevents unneeded updates resulting from variant changes
		// This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
		if (!this.slider || !this.nextButton) return

		const previousPage = this.currentPage
		if (!this.sliderItemOffset || this.sliderItemOffset === 0) {
			this.currentPage = 1
		} else {
			this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1
		}

		if (this.currentPageElement && this.pageTotalElement) {
			this.currentPageElement.textContent = `${this.currentPage}`
			this.pageTotalElement.textContent = `${this.totalPages}`
		}

		if (this.currentPage != previousPage) {
			if (this.sliderItemsToShow) {
				this.dispatchEvent(this.createSlideChangedEvent())
			} else {
				throw new Error('sliderItemsToShow is undefined')
			}
		}

		if (this.enableSliderLooping) {
			this.setCarousel()
			return
		}

		if (
			this.prevButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length &&
			this.isSlideVisible(this.sliderItemsToShow[0]) &&
			this.slider.scrollLeft === 0
		) {
			this.prevButton.setAttribute('disabled', 'disabled')
		} else if (this.prevButton) {
			this.prevButton.removeAttribute('disabled')
		}

		if (
			this.nextButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length > 0 &&
			this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
		) {
			this.nextButton.setAttribute('disabled', 'disabled')
		} else if (this.nextButton) {
			this.nextButton.removeAttribute('disabled')
		}
		this.setCarousel();
	}

	isSlideVisible(element: HTMLElement, offset = 0) {
		const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset
		return (
			element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
			element.offsetLeft >= this.slider.scrollLeft
		)
	}

	onButtonClick(event: MouseEvent) {
		event.preventDefault()
		const currentTarget = q.rct(event)
		if (!(currentTarget instanceof HTMLButtonElement)) return
		if (!this.sliderItemOffset) return
		if (this.enableSliderLooping && this.totalPages && this.currentPage) {
			const isNext = currentTarget.name === 'next'
			const isAtBoundary = isNext
				? this.currentPage >= this.totalPages
				: this.currentPage <= 1
			if (isAtBoundary) {
				this.setSlidePosition(
					isNext ? 0 : this.sliderItemOffset * (this.totalPages - 1)
				)
				this.setActive()
				this.restartAutoplay()
				return
			}
		}
		const step = currentTarget.dataset.step ? parseInt(currentTarget.dataset.step) : 1
		this.slideScrollPosition =
			currentTarget.name === 'next'
				? this.slider.scrollLeft + step * this.sliderItemOffset
				: this.slider.scrollLeft - step * this.sliderItemOffset
		this.setSlidePosition(this.slideScrollPosition)
		
		this.setActive();
		this.restartAutoplay()
	}

	setSlidePosition(position: number) {
		this.slider.scrollTo({
			left: position,
		})
	}

	loadMedia() {
		if (this.mediaLoaded) return
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager) return
		this.mediaLoaded = true
		void mediaManager.playAllInContainer(this, {
			includeEventDriven: false,
		})
	}

	private setupAutoplay() {
		if (this.localName !== SliderComponent.htmlSelector) return
		const autoplayEnabled =
			this.dataset.autoplay === 'true' ||
			this.slider.getAttribute('data-autoplay') === 'true'
		if (!autoplayEnabled) return

		const speed = this.dataset.speed ?? this.slider.dataset.speed
		this.autoplaySpeed = speed ? parseInt(speed, 10) * 1000 : 0
		if (!Number.isFinite(this.autoplaySpeed) || this.autoplaySpeed <= 0) return

		this.autoplayEnabled = true
		this.addEventListener('mouseenter', this.onAutoplayMouseEnterBound)
		this.addEventListener('mouseleave', this.onAutoplayMouseLeaveBound)
		this.addEventListener('focusin', this.onAutoplayFocusInBound)
		this.addEventListener('focusout', this.onAutoplayFocusOutBound)

		if (this.reducedMotion.matches) return
		if (!('IntersectionObserver' in window)) {
			this.playAutoplay()
			return
		}

		this.autoplayObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting) {
					this.playAutoplay()
					return
				}
				this.pauseAutoplay()
			},
			{ threshold: 0.25 }
		)
		this.autoplayObserver.observe(this)
	}

	private playAutoplay() {
		if (!this.autoplayEnabled || this.reducedMotion.matches) return
		this.slider.setAttribute('aria-live', 'off')
		window.clearInterval(this.autoplay)
		this.autoplay = window.setInterval(
			this.autoRotateSlide.bind(this),
			this.autoplaySpeed
		)
	}

	private pauseAutoplay() {
		if (!this.autoplayEnabled) return
		this.slider.setAttribute('aria-live', 'polite')
		window.clearInterval(this.autoplay)
	}

	private restartAutoplay() {
		if (!this.autoplayEnabled) return
		this.pauseAutoplay()
		this.playAutoplay()
	}

	private autoRotateSlide() {
		if (!this.sliderItemOffset || !this.currentPage || !this.totalPages) return
		const isLastSlide = this.currentPage >= this.totalPages
		const slideScrollPosition = isLastSlide
			? 0
			: this.slider.scrollLeft + this.sliderItemOffset
		this.setSlidePosition(slideScrollPosition)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.pauseAutoplay()
		this.autoplayObserver?.disconnect()
		this.removeEventListener('mouseenter', this.onAutoplayMouseEnterBound)
		this.removeEventListener('mouseleave', this.onAutoplayMouseLeaveBound)
		this.removeEventListener('focusin', this.onAutoplayFocusInBound)
		this.removeEventListener('focusout', this.onAutoplayFocusOutBound)
	}
}
